import { pageHandle } from './state';
import type { CameraOpOptions } from '../lib/camera';
import type { LayoutIntent, NodeId } from '../lib/types';
import type { DashboardScreen, PermissionTab } from '../lib/location';
import type { MenuTarget } from '../lib/contextMenu';

/**
 * The imperative surface each region offers the rest of the app: "open yourself".
 *
 * WHY A REGISTRY AND NOT AN IMPORT. A region is a component; asking it to open is asking a
 * mounted tree to change state, which a module cannot do by importing it. The registry inverts
 * that: each region registers a handle when it mounts, and callers ask by name.
 *
 * It also breaks a cycle that would otherwise be real. `openDoc()` lives with the map's state
 * because the doc view's state is map state; the doc overlay calls `closeDoc()`; if the two
 * imported each other directly the module graph would have `actions -> DocOverlay -> actions`
 * with a component in the middle. This is the same arrangement the migration used through
 * `window.InfoStationReact`, made a module — typed, greppable, and not on the global object.
 *
 * EVERY MEMBER IS OPTIONAL because a handle exists only while its region is mounted, and
 * several regions render nothing until they are opened. A call to a region that is not there
 * is a no-op, which is the honest behaviour: nothing to close.
 */

export interface MapHandle {
  /**
   * `autoLayout()` — queue the canonical tidy-tree pass.
   *
   * The INTENT is required, never defaulted (plan Part 2). Positions are shared server state
   * and a value-diff cannot tell a drag from a collapse, so every caller has to say whether
   * the coordinates this pass produces are everybody's business or only this person's. A
   * default would make the wrong answer the quiet one, and a wrong answer here is either a
   * map that moves under other people or an edit that is silently never saved.
   */
  relayout: (intent: LayoutIntent) => void;
  /** Re-derive whether the documentation rows are showing (V0.42.2) — `evaluateDocsExpand()`,
   *  which folds the zoom threshold and the `docsPinned` toggle into one flag. */
  syncDocsExpand: () => void;
  /** `resolveCollisions(anchorId)` — a separation pass around one held-fixed subtree. */
  settle: (anchorId: NodeId | null, intent: LayoutIntent) => void;
  /** `fit()` — frame the whole visible map. */
  fit: (opts?: CameraOpOptions) => void;
  /** `centerOnNodes(ids)` — frame a set of nodes, for the chatbot's citations. */
  centerOnNodes: (ids: NodeId[], opts?: CameraOpOptions) => void;
  zoomBy: (factor: number) => void;
  /** The day-scoped view record's camera, read by `saveViewState()`… */
  getCamera: () => { x: number; y: number; k: number };
  /** …and written by `restoreViewState()`. `animate` is the filter clearing and returning. */
  setCamera: (v: { x: number; y: number; k: number }, opts?: { animate?: boolean }) => void;
  /**
   * Put a node's label into its in-place editor.
   *
   * `addChild()` asks for this the moment it has created a node, which is BEFORE React has
   * rendered it. That is fine and deliberate: this is state, not a call on an element, so the
   * node arrives already in edit mode and focuses itself when it does.
   */
  beginRename: (id: NodeId) => void;
}

export interface PopupOpenOptions {
  /**
   * Called ONCE, from the layout effect that shows the popup, with the freshly mounted seat —
   * the node-shaped chip at the top of the header the node flies into (V0.47) — or `null` when
   * this open produced no fresh seat (it was routed to a refresh, or the node is gone). Never
   * fired by `refresh()`: a refresh keeps the draft AND the camera, and `51-popup-refresh`
   * asserts both.
   */
  onSeat?: (seat: HTMLElement | null) => void;
}

export interface PopupHandle {
  /** Remount on `id`. `open(id)` on the node already showing with a dirty draft is a refresh. */
  open: (id: NodeId, opts?: PopupOpenOptions) => void;
  /** Re-read the SHAPE (read-only vs editable, which AI panel) without remounting. */
  refresh: (id: NodeId) => void;
  /**
   * `fly`: the node returns to the map from its seat before the popup goes — the region's own
   * closes (scrim, Cancel, Save, Escape). Default is instant: the router's closes are one half
   * of a navigation that moves the camera for its own reasons, and "node vanished" has no
   * node to return to.
   */
  close: (opts?: { fly?: boolean }) => void;
}

export interface Handles {
  map?: MapHandle;
  toast?: { show: () => void };
  /** The About panel. `subscribe` is how the version line follows its open state. */
  about?: { subscribe: (l: (open: boolean) => void) => () => void; toggle: () => void; close: () => void };
  /** `refresh(id)` re-reads the popup's SHAPE (read-only vs editable, which AI panel) without
   *  remounting it, so an unsaved draft survives. `open(id)` on a node already showing is a
   *  remount and resets the draft — which is the point of having two verbs. */
  popup?: PopupHandle;
  /** The right-click menu (V0.54): open it on a target at a screen point, or close it. */
  contextMenu?: { open: (target: MenuTarget, clientX: number, clientY: number) => void; close: () => void };
  docPicker?: { open: (nodeId: NodeId, key: string) => void; close: () => void };
  logDetail?: { open: (id: string) => void; close: () => void };
  targets?: { open: () => void; close: () => void };
  liveVerify?: { start: (nodeId: NodeId) => void; close: () => void };
  aidocConfig?: { open: () => void; close: () => void };
  /** The permissions page. `open(tab)` lands on that tab; `setTab` switches while open (V0.54.5). */
  permissions?: { open: (tab: PermissionTab) => void; close: () => void; setTab: (tab: PermissionTab) => void };
  /** The dashboard (V0.44.1: five tabs since V0.50.2, each an admin-screen word). `open(tab?)`
   *  lands on the tab named, or on the one last shown, and returns where it landed so the
   *  caller can report the right URL. The AI console has been its `'ai-docs'` tab since
   *  V0.44.1 and the document log its `'logs'` tab since V0.49 — neither has a handle of its
   *  own any more. `opts.logsView` picks which of the Logs tab's two views opens, so
   *  `openLogs()` can still mean the document history it has always meant; `opts.nodeId` hands
   *  the Projects tab the node whose project to open on (V0.50.2). */
  dashboard?: {
    open: (
      tab?: DashboardScreen,
      opts?: { logsView?: 'actions' | 'history'; nodeId?: NodeId },
    ) => DashboardScreen;
    close: () => void;
  };
  help?: { open: () => void; close: () => void };
  /** The privacy page. Openable pre-login (from the sign-in card) as well as from About. */
  privacy?: { open: () => void; close: () => void };
}

/** The one registry. Mutated as regions mount; read at call time, never captured. */
export const handles: Handles = {};

/**
 * Register a region's handle. Called from the region's own mount effect.
 *
 * Mirrored onto the app's external handle too, because the surrounding suite asks for regions
 * by the same names — `window.InfoStationReact.about.toggle()` is how the version line in an
 * embedding page opens the About panel, and it is what `e2e/17-bridge-and-toast.spec.ts`
 * checks is there.
 */
export function registerHandle<K extends keyof Handles>(name: K, handle: Handles[K]): void {
  handles[name] = handle;
  pageHandle[name] = handle;
}
